<template>
  <v-app>
    <!-- Modern Header -->
    <v-app-bar
      app
      flat
      height="64"
      class="modern-header"
      style="
        background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      "
    >
      <v-app-bar-nav-icon @click="drawer = !drawer" color="primary" />

      <!-- Brand Title -->
      <v-toolbar-title class="text-h5 font-weight-bold text-primary">
        <v-icon class="mr-2" color="primary">$mdiChartLine</v-icon>
        DipSIP
      </v-toolbar-title>

      <v-spacer />

      <!-- Market Selector -->
      <v-select
        v-model="countrySelected"
        :items="countries"
        label="Market"
        variant="solo"
        density="compact"
        hide-details
        class="mx-2"
        style="width: auto; min-width: unset; max-width: fit-content"
      ></v-select>

      <!-- Search field -->
      <!-- Replace your existing v-autocomplete with this version -->
      <v-autocomplete
        v-model="selectedCompany"
        :items="companies"
        :loading="isLoading"
        label="Search companies.."
        density="compact"
        variant="solo"
        hide-details
        return-object
        item-title="name"
        item-value="symbol"
        class="flex-grow-1 mx-2"
        :custom-filter="() => true"
        no-filter
        @update:model-value="navigateToCompany"
        @update:search="onSearchInput"
      >
        <template v-slot:item="{ props, item }">
          <v-list-item
            v-bind="props"
            :title="item.raw.name"
            :subtitle="item.raw.symbol"
          >
          </v-list-item>
        </template>
      </v-autocomplete>

      <v-spacer />

      <!-- User menu -->
      <div v-if="$store.getters.isAuthenticated">
        <v-menu location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props" class="mr-2">
              <v-avatar color="primary" size="36">
                <v-img
                  v-if="
                    $store.getters.currentUser &&
                    $store.getters.currentUser.photoURL
                  "
                  :src="$store.getters.currentUser.photoURL"
                />
                <v-icon v-else>$mdiAccount</v-icon>
              </v-avatar>
            </v-btn>
          </template>
          <v-list class="modern-menu">
            <v-list-item @click="$router.push('/profile')" class="menu-item">
              <template v-slot:prepend>
                <v-icon color="primary">$mdiAccountCircle</v-icon>
              </template>
              <v-list-item-title>View Profile</v-list-item-title>
            </v-list-item>
            <v-divider />
            <v-list-item @click="handleLogout" class="menu-item">
              <template v-slot:prepend>
                <v-icon color="error">$mdiLogout</v-icon>
              </template>
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div v-else>
        <v-btn color="primary" to="/login" variant="elevated" class="login-btn">
          Login
        </v-btn>
      </div>
    </v-app-bar>

    <!-- Modern Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
      width="280"
      class="modern-drawer"
      style="background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
    >
      <!-- User Profile Section -->
      <div
        v-if="$store.getters.isAuthenticated"
        class="user-profile-section pa-4"
      >
        <v-card
          class="user-card pa-4"
          elevation="0"
          style="
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            border-radius: 12px;
          "
        >
          <div class="d-flex align-center">
            <v-avatar
              size="48"
              class="user-avatar mr-3"
              style="border: 2px solid rgba(255, 255, 255, 0.3)"
            >
              <v-img
                v-if="
                  $store.getters.currentUser &&
                  $store.getters.currentUser.photoURL
                "
                :src="$store.getters.currentUser.photoURL"
              />
              <v-icon v-else color="white">$mdiAccount</v-icon>
            </v-avatar>
            <div>
              <div class="user-name text-white font-weight-bold">
                {{ $store.getters.currentUser?.displayName || "User" }}
              </div>
              <div class="user-portfolio text-white" style="opacity: 0.9">
                Portfolio Manager
              </div>
            </div>
          </div>
        </v-card>
      </div>

      <!-- Navigation Menu -->
      <div class="navigation-section pa-2">
        <v-list class="bg-transparent">
          <v-list-subheader
            class="section-title font-weight-bold text-grey-darken-2 mb-2"
          >
            MAIN MENU
          </v-list-subheader>

          <template v-for="(item, i) in menuItems" :key="i">
            <!-- Regular Navigation Items -->
            <v-list-item
              v-if="!item.dropdown"
              :to="item.to"
              :prepend-icon="item.icon"
              exact
              class="nav-item ma-1"
              rounded="lg"
            >
              <v-list-item-title class="font-weight-medium">
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>

            <!-- Dropdown Navigation Items -->
            <v-list-group v-else :value="item.title" class="nav-dropdown ma-1">
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  :prepend-icon="item.icon"
                  class="nav-item"
                  rounded="lg"
                >
                  <v-list-item-title class="font-weight-medium">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>

              <!-- Sub-menu Items -->
              <v-list-item
                v-for="(subItem, j) in item.items"
                :key="`${i}-${j}`"
                :to="subItem.to"
                :prepend-icon="subItem.icon"
                class="nav-sub-item"
                rounded="lg"
              >
                <v-list-item-title class="font-weight-medium">
                  {{ subItem.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list-group>
          </template>
        </v-list>
      </div>
    </v-navigation-drawer>
    <!-- Main Content -->
    <v-main>
      <router-view v-slot="{ Component }">
        <v-fade-transition mode="out-in">
          <component :is="Component" />
        </v-fade-transition>
      </router-view>
    </v-main>

    <!-- Modern Chat FAB -->
    <v-btn
      v-if="$route.path !== '/assistant'"
      class="chat-fab"
      color="primary"
      icon
      size="56"
      elevation="8"
      @click="goToChat"
    >
      <v-icon size="28">$mdiMessageText</v-icon>
    </v-btn>
    <!-- Modern Footer -->
    <v-footer class="modern-footer pa-6">
      <v-container>
        <v-row justify="space-between" align="center">
          <v-col cols="12" md="6">
            <div class="d-flex align-center mb-3">
              <v-icon color="primary" size="28" class="mr-2"
                >$mdiChartLine</v-icon
              >
              <h3 class="text-h6 font-weight-bold text-primary">DipSIP</h3>
            </div>
            <p class="text-body-2 text-grey-darken-1 mb-0">
              An NLP/SLM based Capital Markets Query tool allowing adhoc
              queries, notifications, and strategies on live and historical data
              across stocks, indexes, commodities, derivatives, and crypto.
            </p>
          </v-col>

          <v-col cols="12" md="6">
            <v-row>
              <v-col cols="6">
                <h4
                  class="text-subtitle-1 font-weight-bold mb-3 text-grey-darken-2"
                >
                  Legal
                </h4>
                <div class="d-flex flex-column">
                  <a href="#" class="footer-link mb-2">Terms of Service</a>
                  <a href="#" class="footer-link mb-2">Privacy Policy</a>
                  <a href="#" class="footer-link mb-2">Cookie Policy</a>
                </div>
              </v-col>

              <v-col cols="6">
                <h4
                  class="text-subtitle-1 font-weight-bold mb-3 text-grey-darken-2"
                >
                  Contact
                </h4>
                <div class="d-flex flex-column">
                  <div class="d-flex align-center mb-2">
                    <v-icon size="16" class="mr-2" color="grey"
                      >$mdiEmail</v-icon
                    >
                    <span class="text-body-2">admin@dipsip.co.in</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <v-row align="center">
          <v-col cols="12" class="text-center">
            <p class="text-body-2 text-grey-darken-1 mb-0">
              © {{ new Date().getFullYear() }} Aidea Solutions Private Limited.
              All rights reserved.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>
import debounce from "lodash/debounce";
import {
  signOutUser,
  verifyToken,
  initializeAuth,
  listenForAuthChanges,
} from "./firebase/auth";
import { mapGetters } from "vuex";

export default {
  name: "App",
  data() {
    return {
      // Application state
      isLoading: false,
      selectedCompany: "",
      drawer: false,
      search: "",
      searchText: "",
      authVerified: false,
      companies: [],

      // Navigation menu configuration
      menuItems: [
        // Core functionality
        {
          title: "Home",
          icon: "$mdiHome",
          to: "/",
        },
        {
          title: "Research Agent",
          icon: "$mdiAccountSchool",
          to: "/assistant",
        },
        {
          title: "Dip SIP",
          icon: "$mdiDipSwitch",
          to: "/dipsip",
        },

        // Analysis & Portfolio
        {
          title: "Shareholding",
          icon: "$mdiAccountGroup",
          to: "/shareholding",
        },
        {
          title: "ETFs",
          icon: "$mdiFinance",
          to: "/etfList",
        },
        {
          title: "Indexes",
          icon: "$mdiBank",
          to: "/indexes",
        },

        // Trading instruments
        {
          title: "Futures",
          icon: "$mdiChartLine",
          to: "/futures",
        },
        {
          title: "Options",
          icon: "$mdiCalculatorVariant",
          to: "/options",
        },

        // Trading Agent with sub-menu
        {
          title: "Trading Agent",
          icon: "$mdiAccountTie",
          dropdown: true,
          items: [
            {
              title: "Indicators",
              icon: "$mdiChartLineVariant",
              to: "/tradingagent/indicators",
            },
            {
              title: "Strategies",
              icon: "$mdiStrategy",
              to: "/tradingagent/strategies",
            },
          ],
        },

        // Live data & feedback
        {
          title: "Live",
          icon: "$mdiFinance",
          to: "/livemarkets",
        },
        {
          title: "Feedback",
          icon: "$mdiMessageDraw",
          to: "/feedback",
        },
      ],

      // Market configuration
      countries: [
        { title: "India", value: "IN" },
        { title: "USA", value: "US" },
        { title: "Singapore", value: "SG" },
        { title: "Dubai", value: "AE" },
        { title: "Germany", value: "DE" },
      ],
    };
  },
  computed: {
    ...mapGetters(["isAuthenticated", "currentUser", "country"]),
    countrySelected: {
      get() {
        return this.$store.getters.country;
      },
      set(value) {
        this.$store.commit("setCountry", value);
      },
    },
  },
  watch: {},
  async mounted() {
    await initializeAuth(); // ensures persistence is respected

    listenForAuthChanges(async (user) => {
      if (user) {
        // Firebase already logged in user, now get fresh token
        const token = await user.getIdToken(true);
        localStorage.setItem("token", token);

        const result = await verifyToken();
        /*if (result.isValid) {
          await this.fetchCompanies();
        }*/
      }
      this.authVerified = true;
    });
  },
  methods: {
    onSearchInput: debounce(async function (searchText) {
      if (!searchText || searchText.length < 3) {
        this.companies = [];
        return;
      }

      this.isLoading = true;
      try {
        const response = await fetch(
          `/api/company/list?country=${
            this.countrySelected
          }&q=${encodeURIComponent(searchText)}`
        );
        const data = await response.json();
        this.companies = data;
      } catch (error) {
        console.error("Error fetching filtered companies:", error);
      } finally {
        this.isLoading = false;
      }
    }, 300),
    async handleLogout() {
      try {
        await signOutUser();
        // The store will be updated in signOutUser function
        this.$router.push("/");
      } catch (error) {
        console.error("Logout error:", error);
      }
    },
    goToChat() {
      this.$router.push("/assistant");
    },
    navigateToCompany(company) {
      // Add validation to prevent empty/null/undefined values
      if (!company || !company.symbol || company.symbol.trim() === "") {
        console.log("navigateToCompany called with invalid company:", company);
        return; // Exit early - this prevents the buggy API call
      }
      // Navigate to the company details page
      this.$router.push(`/company/${company.symbol}`);

      // Reset the selection after navigation
      this.$nextTick(() => {
        this.selectedCompany = null;
      });
    },
    customFilter(item, queryText, itemText) {
      if (queryText === "") return true;

      const searchText = queryText.toLowerCase();
      const companyName = item.name.toLowerCase();
      const companySymbol = item.symbol.toLowerCase();

      return (
        companyName.includes(searchText) || companySymbol.includes(searchText)
      );
    },

    /*async fetchCompanies() {
      this.isLoading = true;
      this.companies = []
      try {
        const response = await fetch(`/api/company/list`);
        const data = await response.json();
        for (const company of data){
          this.companies.push(company)
        }
        //this.companies = data;
        
        // For now we're using the static list above
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        this.isLoading = false;
      }
    }*/
  },
};
</script>
<style scoped>
/* Modern App Styles */
.v-application {
  font-family: "Inter", "Roboto", sans-serif;
}

/* Header Styles */
.modern-header {
  backdrop-filter: blur(20px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.market-selector {
  background: rgba(59, 130, 246, 0.05);
  border-radius: 8px;
}

.search-field {
  max-width: 350px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 8px;
}

.login-btn {
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
}

/* Menu Styles */
.modern-menu {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.menu-item {
  border-radius: 8px;
  margin: 4px 8px;
}

/* Navigation Drawer Styles */
.modern-drawer {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.user-profile-section {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.user-card {
  color: white;
}

.user-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 2px;
}

.user-portfolio {
  font-size: 0.875rem;
  opacity: 0.9;
}

.navigation-section {
  flex: 1;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 0 16px;
}

.nav-item {
  margin: 2px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

/* ========================================
   Navigation Styles
   ======================================== */

/* Main Navigation Items */
.nav-item {
  border-radius: 12px;
  margin: 2px 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #64748b;
}

.nav-item:hover {
  background: rgba(59, 130, 246, 0.08);
  transform: translateX(4px);
  color: #3b82f6;
}

.nav-item.v-list-item--active {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
  font-weight: 600;
}

/* Dropdown Navigation Styles */
.nav-dropdown {
  background: transparent !important;
}

.nav-dropdown > .v-list-item {
  margin-left: 0 !important;
  padding-left: 16px !important;
}

/* Sub-menu Items */
.v-list-group__items {
  padding-left: 0 !important;
}

.v-list-group__items .v-list-item {
  padding-left: 56px !important;
  margin-left: 0 !important;
}

.nav-sub-item {
  border-radius: 8px;
  margin: 2px 0 2px 24px !important;
  transition: all 0.2s ease;
  background: rgba(248, 250, 252, 0.5);
  color: #64748b;
}

.nav-sub-item:hover {
  background: rgba(59, 130, 246, 0.06);
  transform: translateX(2px);
  color: #3b82f6;
}

.nav-sub-item.v-list-item--active {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.nav-sub-item .v-list-item-title {
  font-size: 0.875rem;
  font-weight: 500;
}

/* ========================================
   Chat FAB Styles
   ======================================== */
/* Chat FAB */
.chat-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.chat-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.4);
}

/* Footer Styles */
.modern-footer {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: auto;
}

.footer-link {
  color: #64748b;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #3b82f6;
  text-decoration: none;
}

/* Main Content */
.v-main {
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}

/* Responsive Design */
@media (max-width: 960px) {
  .search-field {
    max-width: 250px;
  }
}

@media (max-width: 600px) {
  .search-field {
    max-width: 200px;
  }

  .market-selector {
    width: 100px;
  }

  .chat-fab {
    bottom: 16px;
    right: 16px;
  }
}
</style>
